import { RefObject, useEffect, useState } from 'react';

// Starting points from the design's "Secondary sticky navigation" spec, to
// tune by feel. Hiding takes about twice the intent that revealing does, so
// the bar gets out of the way on a deliberate scroll down and comes back on a
// small scroll up.
const ALWAYS_VISIBLE_UNTIL = 60;
const HIDE_AFTER = 40;
const REVEAL_AFTER = 20;
const IGNORE_BELOW = 10;

// Whether a sticky bar is hidden, from the page's scroll direction: hidden
// after scrolling down a while, shown again by scrolling up. It stays shown
// while something in it has focus or an open menu. `resetKey` shows it again
// when it changes (e.g. on switching tabs).
export const useHideOnScroll = (
  ref: RefObject<HTMLElement | null>,
  resetKey?: unknown,
) => {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setHidden(false);
  }, [resetKey]);

  useEffect(() => {
    // Scroll position, direction and accumulated distance live here, not in
    // state: state only changes when the bar switches between shown and hidden.
    let lastY = window.scrollY;
    let direction = 0;
    let accumulated = 0;
    let frame = 0;

    const isBusy = () => {
      const bar = ref.current;
      return (
        !!bar &&
        (bar.matches(':focus-within') ||
          !!bar.querySelector('[aria-expanded="true"]'))
      );
    };

    const update = () => {
      frame = 0;
      const y = window.scrollY;
      const delta = y - lastY;
      if (y <= ALWAYS_VISIBLE_UNTIL || isBusy()) {
        lastY = y;
        accumulated = 0;
        setHidden(false);
        return;
      }
      // Small jitters (a trackpad's) don't count, nor change direction.
      if (Math.abs(delta) < IGNORE_BELOW) return;
      lastY = y;
      const next = delta > 0 ? 1 : -1;
      if (next !== direction) {
        direction = next;
        accumulated = 0;
      }
      accumulated += Math.abs(delta);
      if (direction > 0 && accumulated >= HIDE_AFTER) setHidden(true);
      if (direction < 0 && accumulated >= REVEAL_AFTER) setHidden(false);
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref]);

  return hidden;
};
